import {
	createContext,
	type Dispatch,
	type ReactNode,
	useCallback,
	useContext,
	useReducer,
} from "react";
import {
	type DataGridAction,
	type DataGridState,
	dataGridReducer,
	initialDataGridState,
} from "./reducer";

const DataGridStateCtx = createContext<DataGridState | null>(null);
const DataGridDispatchCtx = createContext<Dispatch<DataGridAction> | null>(null);

type DataGridStateProviderProps = {
	initialExpanded?: Iterable<string>;
	children: ReactNode;
};

/** Manages grid mechanics: expansion, active-cell focus, and inline editing draft state.
 *
 * Extension pattern: domain-specific grids (e.g. FinancialDataGrid) should wrap DataGrid
 * with their own state provider that owns domain concerns (cell overrides, view mode, etc.)
 * and let DataGridStateProvider own grid mechanics. The domain provider renders inside
 * DataGrid's tree so toolbar components can reach both layers via their respective hooks. */
export const DataGridStateProvider = ({
	initialExpanded,
	children,
}: DataGridStateProviderProps) => {
	const [state, dispatch] = useReducer(
		dataGridReducer,
		{ expanded: initialExpanded },
		initialDataGridState,
	);
	return (
		<DataGridStateCtx.Provider value={state}>
			<DataGridDispatchCtx.Provider value={dispatch}>{children}</DataGridDispatchCtx.Provider>
		</DataGridStateCtx.Provider>
	);
};

export const useDataGridState = (): DataGridState => {
	const ctx = useContext(DataGridStateCtx);
	if (!ctx) throw new Error("useDataGridState must be used inside DataGrid");
	return ctx;
};

export const useDataGridDispatch = (): Dispatch<DataGridAction> => {
	const ctx = useContext(DataGridDispatchCtx);
	if (!ctx) throw new Error("useDataGridDispatch must be used inside DataGrid");
	return ctx;
};

export type DataGridExpansion = {
	expanded: Set<string>;
};

export type DataGridExpansionDispatch = {
	toggleRow: (rowId: string) => void;
	setExpanded: (rowIds: Iterable<string>) => void;
};

/** Purpose-built hook for components (e.g. toolbars) that need to drive row expansion
 *  from inside the DataGrid tree, without reaching into raw state/dispatch. */
export const useDataGridExpansion = (): DataGridExpansion => {
	const { expanded } = useDataGridState();
	return { expanded };
};

export const useDataGridExpansionDispatch = (): DataGridExpansionDispatch => {
	const dispatch = useDataGridDispatch();
	const toggleRow = useCallback(
		(rowId: string) => dispatch({ type: "toggle_row", rowId }),
		[dispatch],
	);
	const setExpanded = useCallback(
		(rowIds: Iterable<string>) => dispatch({ type: "set_expanded", rowIds }),
		[dispatch],
	);
	return { toggleRow, setExpanded };
};
